/* ============================================================== */
/*                                                                */
/*   resultModelV4 · domain model ของหน้า "Result Summary" (HIS V4) */
/*                                                                */
/*   เป็น pure file: ไม่มี import จาก issara-sdk / react-dom        */
/*   (sequence ResultSummaryV4 เป็นฝ่ายเดียวที่เรียก API จริง)      */
/*                                                                */
/*   contract ตรวจด้วย curl กับ staging API แล้ว (ดู README          */
/*   หัวข้อ "contract ของ result summary") และเทียบกับ HIS V3:      */
/*     HISV3/LAB/sequence/HistoryCentralLab.ts:57-96               */
/*       CentralLabOrderList.list({patient}) แล้ว                  */
/*       CentralLabOrderItemResult.list({order_id}) ต่อออเดอร์      */
/*     HISV3/IME/IMEInterface.ts -> GET /apis/IME/list_result/?encounter= */
/*     HISV3/LAB/sequence/OPDLabSummary.ts:521-560                 */
/*       CentralLabResultComparable.post({patient, products, from_date}) */
/*                                                                */
/* ============================================================== */

import { asBoolean, asNumber, asText, decodeHtml, itemsOf, type Dict } from "./examModelV4";

/* ============================================================== */
/*   SECTION 1 · helper เล็กน้อย                                   */
/* ============================================================== */

/**
 * field หลายตัวใน LAB/IME serializer ตอบกลับเป็น HTML (เช่น order_name "<b>[HM003] …</b>",
 * result ของ Imaging แทรก <button onclick="window.open(PACS)">)
 * V4 ต้อง render เป็นข้อความ/ลิงก์ธรรมดา ห้าม dangerouslySetInnerHTML
 */
export const stripHtml = (value: unknown): string =>
  decodeHtml(value)
    .replace(/<[^>]*>/g, " ")
    .replace(/\s+/g, " ")
    .trim();

/** value ของผลแลปเป็นได้ทั้ง number และ text */
export const labValueText = (value: unknown): string => {
  if (value === null || value === undefined) return "";
  if (typeof value === "number") return String(value);
  return stripHtml(value);
};

/** ส่งเฉพาะ param ที่มีค่า (DRF ตอบ 400 เมื่อ param เป็น "") */
export const definedParams = (params: Record<string, unknown>): Dict => {
  const out: Dict = {};
  for (const [key, value] of Object.entries(params)) {
    if (value === undefined || value === null) continue;
    if (typeof value === "string" && value.trim() === "") continue;
    out[key] = value;
  }
  return out;
};

/* ============================================================== */
/*   SECTION 2 · ids + params                                      */
/* ============================================================== */

export type ResultIds = {
  encounterId?: number;
  patientId?: number;
  emrId?: number;
};

/** คำสั่งแลปของ encounter นี้ (ผลตรวจราย Encounter) */
export const labOrderListParams = (ids: ResultIds): Dict =>
  definedParams({ encounter: ids.encounterId });

/** คำสั่งแลปทั้งหมดของ patient (ใช้กับ "ผลสะสม" แบบ HistoryCentralLab.ts:57-61) */
export const patientLabOrderListParams = (ids: ResultIds): Dict =>
  definedParams({ patient: ids.patientId });

/** ผลแลปของออเดอร์ (CentralLabOrderItemResult_apps_LAB รับ order_id) */
export const labResultParams = (orderId: number): Dict => ({ order_id: orderId });

/** ผล Imaging ของ encounter (ImagingOrderResultList_apps_IME: ต้องมี encounter หรือ episode) */
export const imagingResultParams = (ids: ResultIds): Dict =>
  definedParams({ encounter: ids.encounterId });

/**
 * body ของ POST /apis/LAB/central-lab-result-comparable/ (graph ย้อนหลังของ lab code)
 * V3: OPDLabSummary.ts:535-546 — columns/items ส่งว่างมา backend คำนวณให้
 * from_date เป็น "DD/MM/YYYY พ.ศ." ตาม V3 (moment format ของ OPDLabSummary)
 */
export const comparablePayload = (args: {
  patientId: number;
  productIds: number[];
  fromDate?: string;
  toDate?: string;
}): Dict => ({
  columns: [],
  items: [],
  patient: args.patientId,
  products: args.productIds,
  from_date: args.fromDate ?? "",
  to_date: args.toDate ?? "",
});

/* ============================================================== */
/*   SECTION 3 · Lab order + specimen                              */
/* ============================================================== */

/** แถวทดสอบในออเดอร์ (order_items) */
export type LabTestRow = {
  id: number;
  labCode: string;
  name: string;
  specimenName: string;
  labDivision: string;
  /** สถานะตาม backend เช่น "รายงานผล" */
  statusLabel: string;
  statusCode: string;
  urgency: string;
  /** barcode ของ tube (specimen) */
  barcode: string;
  isBlood: boolean;
  color: string;
  collectedAt: string;
  reportedAt: string;
  childIds: number[];
  allowedActions: string[];
};

export type LabOrderRow = {
  id: number;
  /** "<b>[HM003] การตรวจหมู่เลือดระบบ ABO</b>" ใน API -> ตัด tag ออก */
  name: string;
  orderedAt: string;
  orderedAtIso: string;
  items: LabTestRow[];
};

const labTestRowFromApi = (row: Dict): LabTestRow => ({
  id: asNumber(row.id) ?? asNumber(row.pk) ?? 0,
  labCode: asText(row.lab_code),
  name: stripHtml(row.name),
  specimenName: asText(row.specimen_name),
  labDivision: asText(row.lab_division),
  statusLabel: asText(row.status_label),
  statusCode: asText(row.status_name),
  urgency: asText(row.urgency),
  barcode: asText(row.code),
  isBlood: asBoolean(row.is_blood),
  color: asText(row.color),
  collectedAt: asText(row.specimen_time),
  reportedAt: asText(row.edited),
  childIds: (Array.isArray(row.children) ? (row.children as Dict[]) : [])
    .map((child) => asNumber(child.id) ?? 0)
    .filter((id) => id !== 0),
  allowedActions: Array.isArray(row.allowed_actions) ? row.allowed_actions.map((item) => `${item}`) : [],
});

/**
 * GET /apis/LAB/central-lab-order/?encounter= -> { total, items: [{ id, order_name, date, order_items: [...] }] }
 * ต้องอ่านด้วย itemsOf() ไม่ใช่ asList() (list endpoint ทุกตัวตอบ { items: [...] })
 */
export function labOrdersFromApi(payload: unknown): LabOrderRow[] {
  return itemsOf(payload).map((row) => ({
    id: asNumber(row.id) ?? asNumber(row.pk) ?? 0,
    name: stripHtml(row.order_name),
    orderedAt: asText(row.date),
    orderedAtIso: asText(row.date_iso),
    items: (Array.isArray(row.order_items) ? (row.order_items as Dict[]) : []).map(labTestRowFromApi),
  }));
}

/** แถว "Specimen" (กล่องเก็บตัวอย่าง) — อ่านจาก specimen_containers ของออเดอร์แลป */
export type SpecimenRow = {
  id: number;
  orderId: number;
  barcode: string;
  specimenName: string;
  labCode: string;
  labTests: string;
  labDivision: string;
  labType: string;
  statusLabel: string;
  statusCode: string;
  isBlood: boolean;
  color: string;
  collectedAt: string;
  received: boolean;
  printed: boolean;
  outlabSentAt: string;
  vendorName: string;
};

export function specimensFromOrderPayload(payload: unknown): SpecimenRow[] {
  return itemsOf(payload).flatMap((order) =>
    (Array.isArray(order.specimen_containers) ? (order.specimen_containers as Dict[]) : []).map((row) => ({
      id: asNumber(row.id) ?? asNumber(row.pk) ?? 0,
      orderId: asNumber(row.order) ?? asNumber(order.id) ?? 0,
      barcode: asText(row.barcode ?? row.code),
      specimenName: asText(row.specimen_name),
      labCode: asText(row.lab_code),
      labTests: stripHtml(row.all_lab_test ?? row.lab_test),
      labDivision: asText(row.lab_division),
      labType: asText(row.lab_type),
      statusLabel: asText(row.status_label),
      statusCode: asText(row.status_name),
      isBlood: asBoolean(row.is_blood),
      color: asText(row.color),
      collectedAt: asText(row.specimen_time),
      received: asBoolean(row.received),
      printed: asBoolean(row.is_printed),
      outlabSentAt: asText(row.outlab_sent_datetime),
      vendorName: asText(row.outlab_vendor_name),
    })),
  );
}

/* ============================================================== */
/*   SECTION 4 · ผลแลป                                             */
/* ============================================================== */

export type LabResultRow = {
  /** id ของ order item (คือ pk ของ central-lab-order-item) */
  orderItemId: number;
  resultId: number | undefined;
  labCode: string;
  name: string;
  value: string;
  unit: string;
  refValue: string;
  flagResult: string;
  comment: string;
  statusName: string;
  statusLabel?: string;
  orderStatus: string;
  reportBy: string;
  reportAt: string;
  specimenAt: string;
  /** ค่าวิกฤต (backend ส่ง min/max มาให้) */
  criticalMin: string;
  criticalMax: string;
  isAuthorized: boolean;
  /** backend `is_confidential` / `name_confidential` (ผลลับ — ต้องใส่รหัสผ่านก่อนดู) */
  isConfidential?: boolean;
  /** panel ของ test ย่อย (backend ส่ง children ซ้อนได้) */
  children: LabResultRow[];
};

const labResultRowFromApi = (row: Dict): LabResultRow => ({
  orderItemId: asNumber(row.id) ?? asNumber(row.pk) ?? 0,
  resultId: asNumber(row.result_id),
  labCode: asText(row.lab_code),
  name: stripHtml(row.product_name ?? row.name),
  value: labValueText(row.value),
  unit: asText(row.unit),
  refValue: asText(row.ref_value),
  flagResult: asText(row.flag_result),
  comment: stripHtml(row.comment),
  statusName: asText(row.status_name),
  statusLabel: asText(row.status_label),
  orderStatus: asText(row.order_status),
  reportBy: stripHtml(row.report_by),
  reportAt: asText(row.report_time),
  specimenAt: asText(row.specimen_time),
  criticalMin: asText(row.critical_value_min),
  criticalMax: asText(row.critical_value_max),
  isAuthorized: asBoolean(row.is_authorized),
  isConfidential: asBoolean(row.is_confidential ?? row.name_confidential),
  children: (Array.isArray(row.children) ? (row.children as Dict[]) : []).map(labResultRowFromApi),
});

/** GET /apis/LAB/central-lab-order-item/result/?order_id= -> { items: [...] } */
export function labResultsFromApi(payload: unknown): LabResultRow[] {
  return itemsOf(payload).map(labResultRowFromApi);
}

/** รวมผลของหลายออเดอร์เป็นรายการเดียว พร้อม flatten children (depth จำกัดกันวงรอบ) */
export function flattenLabResults(orders: LabResultRow[][], depth = 3): LabResultRow[] {
  const out: LabResultRow[] = [];
  /* depth = จำนวนชั้นลูกที่ flattening รวมได้ (1 = พ่อ+ลูก, 2 = รวมหลาน …) กัน recursion ไม่สิ้นสุด */
  const walk = (rows: LabResultRow[], level: number): void => {
    for (const row of rows) {
      out.push(row);
      if (level + 1 <= depth && row.children.length > 0) walk(row.children, level + 1);
    }
  };
  for (const group of orders) walk(group, 0);
  return out;
}

/** ค่าปกติต้อง flagged = NORMAL (backend ส่ง flag_result) — ใช้ทำ icon เตือน */
export const isCriticalResult = (row: LabResultRow): boolean => {
  const flag = row.flagResult.toUpperCase();
  return flag === "HH" || flag === "LL" || flag === "CRITICAL" || flag === "CRITICAL_VALUE" || flag === "PANIC";
};

/** แถวผลแลปสำหรับ UI: ตัดแถวที่ยังไม่ลงผลออกจากรายการผล */
export const reportedLabRows = (rows: LabResultRow[]): LabResultRow[] =>
  rows.filter((row) => row.value !== "" || row.comment !== "");

export const labResultText = (row: LabResultRow): string =>
  [row.value, row.unit].filter((part) => part !== "").join(" ");

/* ============================================================== */
/*   SECTION 5 · ผล Imaging                                        */
/* ============================================================== */

export type ImagingResultRow = {
  id: number;
  orderItemId: number;
  /** เช่น "[80301055] EKG 12 leads" */
  summary: string;
  doctorName: string;
  reportedAt: string;
  orderedAt: string;
  /** text ล้วน (ตัด <button …PACS> ออก) */
  resultText: string;
  impression: string;
  doctorSummary: string;
  /** PACS link — ให้ แสดงเป็นลิงก์เท่านั้น ไม่ใช่ HTML */
  resultUrl: string;
  active: boolean;
};

/** GET /apis/IME/list_result/?encounter= -> { total, items: [...] } */
export function imagingResultsFromApi(payload: unknown): ImagingResultRow[] {
  return itemsOf(payload)
    .filter((row) => row.is_active !== false)
    .map((row) => ({
      id: asNumber(row.id) ?? 0,
      orderItemId: asNumber(row.order_item) ?? 0,
      summary: stripHtml(row.summary),
      doctorName: stripHtml(row.doctor_name),
      reportedAt: asText(row.reported_datetime),
      orderedAt: asText(row.order_time),
      resultText: stripHtml(row.result),
      impression: stripHtml(row.impression),
      doctorSummary: stripHtml(row.doctor_summary_result),
      resultUrl: asText(row.result_url),
      active: asBoolean(row.is_active),
    }));
}

/* ============================================================== */
/*   SECTION 6 · ผลสะสม (graph ย้อนหลังของ lab code)               */
/* ============================================================== */

export type TrendPoint = {
  date: string;
  value: string;
  flag: string;
  comment: string;
  orderItemId: number | undefined;
};

export type TrendSeries = {
  labId: number;
  name: string;
  unit: string;
  refValue: string;
  recommendation: string;
  confidential: boolean;
  /** ลำดับคอลัมน์วันที่ backend ส่งมา */
  dates: string[];
  points: TrendPoint[];
};

/**
 * POST /apis/LAB/central-lab-result-comparable/ ->
 * { columns: ["28/10/2569 09:59:56"], items: [{ lab_id, name, lab_unit, ref_value,
 *   result: { "<date>": { value, flag_result, flag_result_label } }, "<date>": value,
 *   comments: { "<date>": text }, order_item_id: { "<date>": id }, is_confidential }] }
 * backend ยังแถว "for_pdf" ปนมาด้วย -> กรองออก
 */
export function trendFromComparable(payload: unknown, labId: number): TrendSeries | null {
  const data = (payload ?? {}) as Dict;
  const dates = (Array.isArray(data.columns) ? (data.columns as unknown[]) : []).map((item) => `${item}`);
  const rows = itemsOf(data.items).filter((row) => row.for_pdf !== true);
  const row = rows.find((item) => (asNumber(item.lab_id) ?? 0) === labId) ?? rows[0];

  if (row === undefined) return null;

  const resultMap = (row.result ?? {}) as Dict;
  const commentMap = (row.comments ?? {}) as Dict;
  const itemMap = (row.order_item_id ?? {}) as Dict;

  return {
    labId: asNumber(row.lab_id) ?? labId,
    name: stripHtml(row.name),
    unit: asText(row.lab_unit ?? row.unit),
    refValue: asText(row.ref_value),
    recommendation: stripHtml(row.recommendation),
    confidential: asBoolean(row.is_confidential),
    dates,
    points: dates.map((date) => {
      const entry = (resultMap[date] ?? {}) as Dict;
      return {
        date,
        value: labValueText(entry.value ?? row[date]),
        flag: asText(entry.flag_result_label ?? entry.flag_result),
        comment: stripHtml(commentMap[date]),
        orderItemId: asNumber(itemMap[date]),
      };
    }),
  };
}

/** วันที่ของ backend เช่น "28/10/2569 09:59:56" -> "28/10/2569 [09:59]" (V3 formateDateLab) */
export const trendDateLabel = (date: string): string =>
  date.replace(/(\d{2}:\d{2}):(\d{2})$/, "[$1]");

/** product id ของ test ในออเดอร์ (backend ใช้ products: [product_id] ในการคำนวณ graph) */
export function productIdsOf(items: { productId?: number | null }[]): number[] {
  return items.map((item) => asNumber(item.productId)).filter((id): id is number => id !== null);
}

export type ProcedureRow = {
  id: number;
  name: string;
  icd9cm: string;
  practitionerName: string;
  result: string;
  complication: string;
  status: string;
  createdAt: string;
};

export function proceduresFromApi(payload: unknown): ProcedureRow[] {
  return itemsOf(payload).map((row) => ({
    id: asNumber(row.id) ?? asNumber(row.pk) ?? 0,
    name: stripHtml(row.procedure_name || row.name),
    icd9cm: asText(row.icd9cm_code || row.icd9cm),
    practitionerName: stripHtml(row.practitioner_name || row.doctor_name),
    result: asText(row.result),
    complication: asText(row.complication),
    status: asText(row.status || "เสร็จสิ้น"),
    createdAt: asText(row.created),
  }));
}

export type DrugOrderRow = {
  id: number;
  name: string;
  type: string;
  stage: number;
  orderStatus: string;
  administeredAt?: string;
  administeredBy?: string;
};

/**
 * MAR window date: TPD/medication-record-list-summary/ read query `date` as Buddhist era dd/mm/yyyy
 * and it needs `emr` (not only `encounter`) -- see PatientHistoryV4.LOAD_DETAIL.
 * Accepts "15/09/2569 10:12" or "2026-09-15T10:12...".
 */
export const marDateText = (value: unknown): string => {
  const text = asText(value).trim();
  const slash = /(\d{1,2})\/(\d{1,2})\/(\d{4})/.exec(text);
  if (slash) return `${slash[1]}/${slash[2]}/${slash[3]}`;
  const iso = /(\d{4})-(\d{2})-(\d{2})/.exec(text);
  if (iso) return `${iso[3]}/${iso[2]}/${Number(iso[1]) + 543}`;
  return "";
};

export function drugOrdersFromApi(payload: unknown): DrugOrderRow[] {
  return itemsOf(payload).map((row) => ({
    id: asNumber(row.id) ?? asNumber(row.pk) ?? 0,
    name: stripHtml(row.trade_name || row.drug_name || row.name),
    type: asText(row.type_name || row.type || "ยาใช้ภายใน"),
    stage: asNumber(row.stage) ?? 4,
    orderStatus: asText(row.order_status || "จ่ายแล้ว"),
    administeredAt: asText(row.administered_datetime),
    administeredBy: stripHtml(row.administered_by_name || row.administered_by),
  }));
}

/* ============================================================== */
/*   SECTION 7 · slice ของ sequence ResultSummaryV4                */
/* ============================================================== */

export type ResultV4Slice = {
  sequenceIndex: string | null;
  ids: ResultIds;
  /** order name ของ lab ที่ยังไม่มีผล (ใช้แสดง "รอดูผล") */
  labOrders: LabOrderRow[];
  specimens: SpecimenRow[];
  labResults: LabResultRow[];
  imagingResults: ImagingResultRow[];
  /** series ของ modal "ผลสะสม" (lazy: โหลดเมื่อเปิด modal) */
  trend: TrendSeries | null;
  /** lab (product) id ที่กำลังเปิดดูผลสะสม */
  trendLabId: number | null;
  confidentialUnlocked: boolean;
  procedures: ProcedureRow[];
  drugOrders: DrugOrderRow[];
};

export const ResultIdsInitial: ResultIds = {};

export const emptyResultV4Slice = (): ResultV4Slice => ({
  sequenceIndex: null,
  ids: ResultIdsInitial,
  labOrders: [],
  specimens: [],
  labResults: [],
  imagingResults: [],
  trend: null,
  trendLabId: null,
  confidentialUnlocked: false,
  procedures: [],
  drugOrders: [],
});

/** state อาจถูก RunSequence ล้างเหลือ { sequenceIndex: "START" } ชั่วคราว -> card ต้องอ่านได้ */
export const normalizeResultV4Slice = (
  slice: Partial<ResultV4Slice> | null | undefined,
): ResultV4Slice => {
  if (slice === null || slice === undefined) return emptyResultV4Slice();
  const base = emptyResultV4Slice();

  return {
    ...base,
    ...slice,
    ids: slice.ids ?? base.ids,
    labOrders: Array.isArray(slice.labOrders) ? slice.labOrders : base.labOrders,
    specimens: Array.isArray(slice.specimens) ? slice.specimens : base.specimens,
    labResults: Array.isArray(slice.labResults) ? slice.labResults : base.labResults,
    imagingResults: Array.isArray(slice.imagingResults) ? slice.imagingResults : base.imagingResults,
    trend: slice.trend ?? null,
    trendLabId: slice.trendLabId ?? null,
    confidentialUnlocked: asBoolean(slice.confidentialUnlocked),
    procedures: Array.isArray(slice.procedures) ? slice.procedures : base.procedures,
    drugOrders: Array.isArray(slice.drugOrders) ? slice.drugOrders : base.drugOrders,
  };
};

/** จำนวนรายการที่ใช้แสดงหัวหน้า (design แสดง "ผลแลป n · ผล Imaging n") */
export const resultSummaryCounts = (slice: ResultV4Slice): { lab: number; imaging: number } => ({
  lab: reportedLabRows(slice.labResults).length,
  imaging: slice.imagingResults.length,
});

